import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin } from 'lucide-react';
import type { Address } from '@/data/types';
import { COUNTRIES } from '@/lib/country';
import { useT } from '@/lib/i18n';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  initial?: Address;
  onSave: (a: Address) => void;
  onGeocode?: (a: Address) => Promise<{ lat: number; lng: number } | null>;
}

export function AddressDialog({
  open,
  onOpenChange,
  title,
  initial,
  onSave,
  onGeocode,
}: Props) {
  const t = useT();
  const [a, setA] = useState<Address>(initial ?? {});
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Address>(k: K, v: Address[K]) =>
    setA(prev => ({ ...prev, [k]: v }));

  const handleGeocode = async () => {
    if (!onGeocode) return;
    setBusy(true);
    try {
      const r = await onGeocode(a);
      if (r) {
        onSave({ ...a, company: a.company });
        onOpenChange(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('address.enterDetails')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>{t('address.companyName')}</Label>
            <Input
              value={a.company ?? ''}
              onChange={e => set('company', e.target.value)}
            />
          </div>
          <div>
            <Label>{t('address.attn')}</Label>
            <Input
              value={a.attn ?? ''}
              onChange={e => set('attn', e.target.value)}
            />
          </div>
          <div>
            <Label>{t('address.street')}</Label>
            <Input
              value={a.street ?? ''}
              onChange={e => set('street', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label>{t('address.zip')}</Label>
              <Input
                value={a.zip ?? ''}
                onChange={e => set('zip', e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('address.city')}</Label>
              <Input
                value={a.city ?? ''}
                onChange={e => set('city', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>{t('common.country')}</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={a.country ?? ''}
              onChange={e => set('country', e.target.value)}
            >
              <option value="">{t('address.select')}</option>
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>{t('address.phone')}</Label>
              <Input
                value={a.phone ?? ''}
                onChange={e => set('phone', e.target.value)}
              />
            </div>
            <div>
              <Label>{t('address.email')}</Label>
              <Input
                value={a.email ?? ''}
                onChange={e => set('email', e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          {onGeocode && (
            <Button
              type="button"
              variant="outline"
              onClick={handleGeocode}
              disabled={busy}
            >
              <MapPin className="h-4 w-4" />
              {t('address.geocodeSave')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              onSave(a);
              onOpenChange(false);
            }}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
